import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import css from './FocalPointPicker.module.scss';
import Target04Icon from '../../Icons/Target04Icon';
import {colors} from '../../../utils/colors.js';

const FocalPointPicker = ({imageSrc, focalPoint, onChange, helperText}) => {
  const imageRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(null);

  const handleClick = (e) => {
    const rect = imageRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100) / 100;
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100) / 100;
    onChange({x, y});
  }

  const handleMouseMove = (e) => {
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setHoverPosition({x, y});
  }

  const handleReset = () => {
    onChange(null);
  }

  if (!imageSrc) {
    return null;
  }

  const hintText = focalPoint
    ? 'Click to reposition the focal point'
    : 'Click to place the focal point';

  return (
    <div className={css.container}>
      <div
        className={css.imageWrapper}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => { setIsHovering(false); setHoverPosition(null); }}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          alt=""
          className={css.image}
        />
        <div className={css.overlay} />
        <div className={css.hintBadge}>{hintText}</div>
        {isHovering && hoverPosition && !focalPoint &&
          <div
            className={`${css.pin} ${css.pinGhost}`}
            style={{
              left: `${hoverPosition.x}%`,
              top: `${hoverPosition.y}%`,
            }}
          >
            <div className={css.crosshairH} />
            <div className={css.crosshairV} />
            <div className={css.pinRing} />
            <div className={css.pinDot} />
          </div>
        }
        {focalPoint &&
          <div
            className={css.pin}
            style={{
              left: `${focalPoint.x * 100}%`,
              top: `${focalPoint.y * 100}%`,
            }}
          >
            <div className={css.crosshairH} />
            <div className={css.crosshairV} />
            <div className={css.pinRing} />
            <div className={css.pinDot} />
          </div>
        }
      </div>
      <div className={css.footer}>
        <div className={css.footerLeft}>
          <Target04Icon width={14} color={colors.grey64} className={css.footerIcon} />
          <span>{helperText || 'Focal point'}</span>
        </div>
        {focalPoint
          ? <>
              <span className={css.coordinates}>
                {Math.round(focalPoint.x * 100)}%, {Math.round(focalPoint.y * 100)}%
              </span>
              <button type="button" className={css.resetButton} onClick={handleReset}>
                Reset
              </button>
            </>
          : <span className={css.notSet}>Not set</span>
        }
      </div>
    </div>
  )
};

FocalPointPicker.propTypes = {
  imageSrc: PropTypes.string,
  focalPoint: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
};

export default FocalPointPicker;
